import React, { ReactNode } from 'react';
import {
  Box,
  Flex,
  HStack,
  VStack,
  Text,
  Image,
  IconButton,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
} from '@chakra-ui/react';
import { HamburgerIcon, ChevronDownIcon, UnlockIcon } from '@chakra-ui/icons';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';

interface LayoutProps {
  children: ReactNode;
}

const NavItem: React.FC<{ to: string; children: ReactNode; isActive?: boolean }> = ({
  to,
  children,
  isActive,
}) => {
  const bgColor = isActive ? 'blue.500' : 'transparent';
  const textColor = isActive ? 'white' : 'gray.700';

  return (
    <Link to={to}>
      <Box
        px={4}
        py={2}
        borderRadius="md"
        bg={bgColor}
        color={textColor}
        _hover={{ bg: isActive ? 'blue.600' : 'gray.100' }}
        fontWeight={isActive ? 'bold' : 'normal'}
      >
        {children}
      </Box>
    </Link>
  );
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { logout, user, isAdmin, isGuest } = useAuth();
  const { lang, setLang, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    { path: '/dashboard', label: t('nav.home') },
    { path: '/risk-register', label: t('nav.riskRegister') },
    ...(!isGuest ? [{ path: '/risk-register/new', label: t('nav.registerNewRisk') }] : []),
    { path: '/departments', label: t('nav.departments') },
    ...(isAdmin ? [{ path: '/users', label: t('nav.users') }] : []),
    ...(isAdmin ? [{ path: '/roles', label: t('nav.roles') }] : []),
  ];

  return (
    <Box minH="100vh">
      <Box bg="white" px={4} shadow="sm" position="sticky" top={0} zIndex={10}>
        <Flex h={16} alignItems="center" justifyContent="space-between">
          <HStack spacing={8} alignItems="center">
            <IconButton
              aria-label="Open menu"
              icon={<HamburgerIcon />}
              variant="ghost"
              display={{ md: 'none' }}
              onClick={onOpen}
            />
            <HStack spacing={3} alignItems="center">
              <Image
                src={process.env.PUBLIC_URL + "/Mahidol_U.png"}
                alt="Mahidol University logo"
                boxSize="10"
                objectFit="contain"
                fallbackSrc=""
              />
              <VStack spacing={0} alignItems="flex-start" lineHeight={1.05}>
                <Text fontWeight="bold" fontSize="xl" color="blue.600">
                  {t('brand.name')}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {t('brand.subtitle')}
                </Text>
              </VStack>
            </HStack>
            <HStack as="nav" spacing={4} display={{ base: 'none', md: 'flex' }}>
              {menuItems.map((item) => (
                <NavItem key={item.path} to={item.path} isActive={location.pathname === item.path}>
                  {item.label}
                </NavItem>
              ))}
            </HStack>
          </HStack>

          <HStack spacing={3}>
            <HStack spacing={1} border="1px solid" borderColor="gray.200" borderRadius="md" p={0.5}>
              <Button
                size="sm"
                h={7}
                px={2}
                variant={lang === 'th' ? 'solid' : 'ghost'}
                colorScheme={lang === 'th' ? 'blue' : 'gray'}
                onClick={() => setLang('th')}
              >
                {t('lang.thai')}
              </Button>
              <Button
                size="sm"
                h={7}
                px={2}
                variant={lang === 'en' ? 'solid' : 'ghost'}
                colorScheme={lang === 'en' ? 'blue' : 'gray'}
                onClick={() => setLang('en')}
              >
                {t('lang.english')}
              </Button>
            </HStack>
            <Menu>
              <MenuButton as={Button} rightIcon={<ChevronDownIcon />}>
                {user?.full_name || t('common.account')}
              </MenuButton>
              <MenuList>
                <MenuItem icon={<UnlockIcon />} onClick={handleLogout}>
                  {t('common.logout')}
                </MenuItem>
              </MenuList>
            </Menu>
          </HStack>
        </Flex>
      </Box>

      <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            <HStack spacing={3} alignItems="center">
              <Image
                src={process.env.PUBLIC_URL + "/Mahidol_U.png"}
                alt="Mahidol University logo"
                boxSize="10"
                objectFit="contain"
              />
              <VStack spacing={0} alignItems="flex-start" lineHeight={1.05}>
                <Text fontWeight="bold" color="blue.600">{t('brand.name')}</Text>
                <Text fontSize="xs" color="gray.500">{t('brand.subtitle')}</Text>
              </VStack>
            </HStack>
          </DrawerHeader>
          <DrawerBody>
            <VStack spacing={4} align="stretch">
              {menuItems.map((item) => (
                <NavItem key={item.path} to={item.path} isActive={location.pathname === item.path}>
                  {item.label}
                </NavItem>
              ))}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <Box p={6}>{children}</Box>
    </Box>
  );
};

export default Layout;
